import Phaser from "phaser";

import { GameBox, GameSaveData } from "../game/GameBox";
import { AudioGate } from "../game/AudioGate";
import { PuzzleModel } from "../game/PuzzleModel";
import { PuzzleView } from "../game/PuzzleView";

type RunState = "idle" | "running" | "finished" | "timeup";
type GameMode = "score" | "free";

const TIME_LIMIT_MS = 180 * 1000; // 180秒

// getData()が既に呼ばれたかどうかを追跡する静的フラグ
let getDataCalled = false;

export default class GameScene extends Phaser.Scene {
  private audio!: AudioGate;
  private model!: PuzzleModel;
  private view!: PuzzleView;
  private shuffleSteps = 200;
  private moves = 0;
  private startMs = 0;
  private elapsedMs = 0;
  private runState: RunState = "idle";
  private animating = false;
  private currentStage = 1;
  private puzzleKeys: string[] = [];
  private boardSizePx = 0;
  private gapPx = 0;
  private gameMode: GameMode = "free";
  private puzzleSize: 2 | 3 | 4 | 5 | 6 = 2; // FREE MODE用のパズルサイズ
  private totalScore = 0;
  private timerEvent?: Phaser.Time.TimerEvent;
  private clearedStages: number[] = [];
  private savedData: GameSaveData | null = null;

  constructor() {
    super("GameScene");
  }

  create() {
    this.audio = new AudioGate(this);
    const { width, height } = this.scale;

    // 背景画像を配置（最背面）
    const bg = this.add.image(width / 2, height / 2, "bg");
    bg.setDisplaySize(width, height);
    bg.setDepth(-100);

    this.boardSizePx = Math.min(width * 0.9, height * 0.72);
    this.gapPx = Math.max(4, Math.floor(this.boardSizePx * 0.01));

    // gamebox.iframe.jsが読み込まれるまで待つ（短縮版：最大1秒）
    // game_start, game_end, game_get_data, game_save のいずれかが存在すれば読み込み完了とみなす
    const waitForGameBox = (retries = 10, delay = 100): Promise<void> => {
      return new Promise((resolve) => {
        const hasApi = 
          typeof window.game_start === "function" ||
          typeof window.game_end === "function" ||
          typeof window.game_get_data === "function" ||
          typeof window.game_save === "function";
        
        if (hasApi || retries === 0) {
          if (!hasApi && retries === 0) {
            console.warn("[GameScene] gamebox.iframe.jsの読み込みを待ちましたが、APIが見つかりませんでした");
          }
          resolve();
          return;
        }
        setTimeout(() => {
          waitForGameBox(retries - 1, delay).then(resolve);
        }, delay);
      });
    };

    // モード選択画面を先に表示（puzzle画像の読み込みを待たない）
    waitForGameBox().then(() => {
      // 保存データを取得（ゲーム初期化時に1回だけ）
      if (!getDataCalled) {
        getDataCalled = true;
        GameBox.getData((data) => {
          this.savedData = data;
          console.log("[GameScene] 保存データを取得:", data ? `Stage ${data.currentStage}` : "なし");

          // パズル画像をランダム順に設定（表示には不要だが、準備しておく）
          this.shufflePuzzleOrder();

          // モード選択画面を即座に表示（puzzle画像の読み込みは後で行う）
          this.time.delayedCall(50, () => {
            const uiScene = this.scene.get("UIScene");
            if (uiScene) {
              uiScene.events.emit("showModeSelect");
            }
          });
        });
      } else {
        // getData()が既に呼ばれている場合は、モード選択画面を表示するだけ
        this.shufflePuzzleOrder();
        this.time.delayedCall(50, () => {
          const uiScene = this.scene.get("UIScene");
          if (uiScene) {
            uiScene.events.emit("showModeSelect");
          }
        });
      }
    });

    // UI側へ参照を渡す
    const bindApi = () => {
      this.scene.get("UIScene").events.emit("bindGame", {
        setMuted: (v: boolean) => this.audio.setMuted(v),
        getMuted: () => this.audio.isMuted(),
        pause: () => this.pauseRun(),
        resume: () => this.resumeRun(),
        restart: () => this.showModeSelect(),
        solve: () => this.instantSolve(),
        setToLastStage: () => this.setToLastStage(),
        start: () => this.startFromButton(),
        nextStage: () => this.prepareNextStage(),
        selectMode: (mode: GameMode) => this.selectMode(mode),
        selectPuzzleSize: (size: 2 | 3 | 4 | 5 | 6) => this.selectPuzzleSize(size),
        getGameMode: () => this.gameMode,
        togglePreview: () => this.togglePreview(),
        getCompletedSizes: () => [], // 全クリデータは保存しないため、常に空配列を返す
        hasSavedData: () => this.hasSavedFreeData(),
        checkSavedData: (callback: (hasData: boolean) => void) => {
          callback(this.hasSavedFreeData());
        },
        continueFromSaved: () => this.continueFromSaved(),
        saveAndExit: () => this.saveAndExit(),
        saveProgressAndEnd: (onSaved: () => void) => this.saveProgressAndEnd(onSaved),
        setTimeTo3Seconds: () => this.setTimeTo3Seconds(),
      });
    };

    // UISceneが先にreadyになった場合は即座にbind
    this.events.on("uiReady", bindApi);
    // UISceneが既に起動済みの場合も考慮して少し遅延してbind
    this.time.delayedCall(50, bindApi);


    this.events.on(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.view?.destroy();
      this.timerEvent?.destroy();
    });
  }

  update(_t: number, _dt: number) {
    if (this.runState !== "running") return;
    this.elapsedMs = this.time.now - this.startMs;

    // スコアモードで時間切れチェック
    if (this.gameMode === "score" && this.elapsedMs >= TIME_LIMIT_MS) {
      this.onTimeUp();
      return;
    }

    const remainingMs = this.gameMode === "score" ? Math.max(0, TIME_LIMIT_MS - this.elapsedMs) : 0;
    this.scene.get("UIScene").events.emit("tick", {
      moves: this.moves,
      elapsedMs: this.elapsedMs,
      remainingMs,
      totalScore: this.totalScore,
      gameMode: this.gameMode,
    });
  }

  /** 必要なpuzzle画像を読み込む */
  private loadPuzzleImages(): Promise<void> {
    return new Promise((resolve) => {
      const imagesToLoad: string[] = [];
      
      // puzzleKeysに含まれる画像を確認
      this.puzzleKeys.forEach((key) => {
        if (!this.textures.exists(key)) {
          imagesToLoad.push(key);
        }
      });

      if (imagesToLoad.length === 0) {
        // 既に全て読み込まれている
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("hideLoading");
        }
        resolve();
        return;
      }

      console.log(`[GameScene] ${imagesToLoad.length}枚のpuzzle画像を読み込みます`);

      // 既存のイベントリスナーをクリア（重複を防ぐ）
      this.load.removeAllListeners("progress");
      this.load.removeAllListeners("complete");

      // 読み込み開始前に進捗を0%に設定
      const uiScene = this.scene.get("UIScene");
      if (uiScene) {
        uiScene.events.emit("updateLoadingProgress", 0);
      }

      // 進捗イベント
      this.load.on("progress", (value: number) => {
        console.log(`[GameScene] 読み込み進捗: ${Math.round(value * 100)}%`);
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("updateLoadingProgress", value);
        }
      });

      // 画像を読み込む
      imagesToLoad.forEach((key) => {
        const puzzleNum = key.replace("puzzle", "");
        this.load.image(key, `assets/img/puzzle${puzzleNum}.png`);
      });

      // 読み込み完了を待つ
      this.load.once("complete", () => {
        console.log("[GameScene] puzzle画像の読み込みが完了しました");
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("hideLoading");
        }
        resolve();
      });

      // 既に読み込み中の場合は待機
      if (this.load.isLoading()) {
        console.warn("[GameScene] 既に読み込み中です。待機します...");
        // 既存の読み込みが完了するのを待つ
        const checkComplete = () => {
          if (!this.load.isLoading()) {
            console.log("[GameScene] 既存の読み込みが完了しました");
            resolve();
          } else {
            // 100ms後に再度チェック
            this.time.delayedCall(100, checkComplete);
          }
        };
        checkComplete();
      } else {
        // 読み込みを開始
        this.load.start();
      }
    });
  }

  /** ステージ情報を更新 */
  private updateStageStatus() {
    const totalStages = this.puzzleKeys.length;
    const clearedCount = this.clearedStages.length;
    const statusText = `Stage ${this.currentStage}/${totalStages}`;
    this.scene.get("UIScene").events.emit("setStatus", { 
      status: statusText,
      currentStage: this.currentStage,
      totalStages: totalStages,
      clearedCount: clearedCount,
      gameMode: this.gameMode
    });
  }

  /** ステージに応じたパズルサイズを返す */
  private getSizeForStage(stage: number): number {
    // FREE MODEの場合は選択したサイズを返す
    if (this.gameMode === "free") {
      return this.puzzleSize;
    }
    // SCORE MODEは従来通り
    if (stage <= 3) return 2;   // Stage 1-3: 2×2
    if (stage <= 10) return 3;  // Stage 4-10: 3×3
    if (stage <= 15) return 4;  // Stage 11-15: 4×4
    if (stage <= 25) return 5;  // Stage 16-25: 5×5
    return 5;                   // SCORE MODE Stage 16-20: 5×5
  }

  /** ステージに応じたスコア倍率を返す */
  private getMultiplierForStage(stage: number): number {
    const size = this.getSizeForStage(stage);
    if (size === 2) return 1.0;  // 2×2: ×1.0
    if (size === 3) return 1.2;  // 3×3: ×1.2
    if (size === 4) return 1.5;  // 4×4: ×1.5
    if (size === 5) return 2.0;  // 5×5: ×2.0
    return 2.5;                  // 6×6: ×2.5
  }

  private shufflePuzzleOrder() {
    // シャッフル用ヘルパー
    const shuffle = (arr: string[]) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    if (this.gameMode === "free") {
      // FREE MODE: 選択したサイズで60ステージ
      // 60枚の画像を全て使用（ランダム順）
      const allKeys: string[] = [];
      for (let i = 1; i <= 60; i++) {
        allKeys.push(`puzzle${i}`);
      }
      shuffle(allKeys);
      this.puzzleKeys = allKeys; // 60ステージ
    } else {
      // SCORE MODE: 20ステージ
      // puzzle1〜puzzle60からランダムで20枚選択
      const allKeys: string[] = [];
      for (let i = 1; i <= 60; i++) {
        allKeys.push(`puzzle${i}`);
      }
      shuffle(allKeys);
      this.puzzleKeys = allKeys.slice(0, 20); // 20ステージ
    }
    this.currentStage = 1;
  }

  private showModeSelect() {
    this.audio.stopAll();
    this.timerEvent?.destroy();
    this.runState = "idle";
    this.animating = false;
    this.moves = 0;
    this.elapsedMs = 0;
    this.totalScore = 0;
    this.clearedStages = [];
    this.shufflePuzzleOrder();

    // Viewを破棄
    if (this.view) {
      this.view.destroy();
    }

    // scene.restart()を呼ぶことで、create()が再実行される
    // 注意: game_start()はここでは呼ばれない（GAME PLAYボタンタップ時にのみ呼ばれる）
    this.scene.restart();
  }

  private selectMode(mode: GameMode) {
    this.gameMode = mode;
    this.totalScore = 0;
    this.currentStage = 1;
    this.clearedStages = [];
    // SCORE MODEの場合は即座にシャッフルして開始、FREE MODEはサイズ選択後にシャッフル
    if (mode === "score") {
      this.shufflePuzzleOrder();
      // 必要なpuzzle画像を読み込んでから開始
      this.loadPuzzleImages().then(() => {
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("hideLoading");
          this.updateStageStatus();
          uiScene.events.emit("tick", {
            moves: 0,
            elapsedMs: 0,
            remainingMs: TIME_LIMIT_MS / 1000,
            totalScore: 0,
            gameMode: this.gameMode,
          });
        }
        this.resetAndWaitStart();
      });
    }
  }

  /** 【FREE MODE非アクティブ化】FREE MODE用: パズルサイズを選択 */
  // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
  private selectPuzzleSize(_size: 2 | 3 | 4 | 5 | 6) {
    // FREE MODEは非アクティブ化されているため、処理をスキップ
    // 復元時は以下のコメントを解除
    return; // 早期リターン（復元時はこの行を削除）
    
    /* 復元時は以下のコメントを解除
    this.puzzleSize = size;
    // サイズを変更する時は、クリア済みステージをリセット（異なるサイズのクリアデータが混在しないようにする）
    this.clearedStages = [];
    this.currentStage = 1;
    this.totalScore = 0;
    this.shufflePuzzleOrder();
    
    // 必要なpuzzle画像を読み込んでから開始
    this.loadPuzzleImages().then(() => {
      const uiScene = this.scene.get("UIScene");
      if (uiScene) {
        uiScene.events.emit("hideLoading");
        this.updateStageStatus();
        uiScene.events.emit("tick", {
          moves: 0,
          elapsedMs: 0,
          remainingMs: this.gameMode === "score" ? TIME_LIMIT_MS / 1000 : 0,
          totalScore: 0,
          gameMode: this.gameMode,
        });
      }
      this.resetAndWaitStart();
    });
    */ // 復元時はこのコメントを解除
  }


  private resetAndWaitStart() {
    this.audio.stopAll();
    this.timerEvent?.destroy();
    this.runState = "idle";
    this.animating = false;
    this.moves = 0;
    this.elapsedMs = 0;

    const n = this.getSizeForStage(this.currentStage);
    
    // パズル画像のインデックスを確認
    const puzzleIndex = this.currentStage - 1;
    if (puzzleIndex < 0 || puzzleIndex >= this.puzzleKeys.length) {
      console.error("[GameScene] resetAndWaitStart: 無効なステージ番号", this.currentStage, "puzzleKeys.length:", this.puzzleKeys.length);
      return;
    }
    const pictureKey = this.puzzleKeys[puzzleIndex];

    // Viewを再構築
    if (this.view) {
      this.view.destroy();
    }
    const { width, height } = this.scale;
    this.view = new PuzzleView({
      scene: this,
      x: width / 2,
      y: height * 0.52,
      pictureKey: pictureKey,
      boardSizePx: this.boardSizePx,
      gapPx: this.gapPx,
      onTilePressed: (tileId) => this.onTilePressed(tileId),
    });

    // モデルが存在しない場合は作成
    if (!this.model) {
      this.model = new PuzzleModel(n);
    } else {
      this.model.resetSolved(n);
    }
    this.model.shuffle(this.shuffleSteps);
    const st = this.model.getState();
    this.view.build(st);

    const remainingMs = this.gameMode === "score" ? TIME_LIMIT_MS : 0;
    this.scene.get("UIScene").events.emit("tick", {
      moves: this.moves,
      elapsedMs: 0,
      remainingMs,
      totalScore: this.totalScore,
      gameMode: this.gameMode,
    });
    this.updateStageStatus();
    this.view.setInputEnabled(false);
    
    // startButtonを表示
    const uiScene = this.scene.get("UIScene");
    if (uiScene) {
      uiScene.events.emit("showStartButton");
    }
    // game_startはゲーム全体の開始時に1回だけ呼ぶため、ここでは呼ばない
  }

  /** Startボタンからゲーム開始 */
  private startFromButton() {
    if (this.runState !== "idle") return;
    
    // viewが存在しない場合は作成（最初のゲーム開始時）
    if (!this.view) {
      // 必要なpuzzle画像を読み込んでからviewを作成
      this.loadPuzzleImages().then(() => {
        const n = this.getSizeForStage(this.currentStage);
        this.model = new PuzzleModel(n);

        if (this.puzzleKeys.length === 0) {
          console.error("[GameScene] startFromButton: puzzleKeysが空です");
          return;
        }

        const { width, height } = this.scale;
        this.view = new PuzzleView({
          scene: this,
          x: width / 2,
          y: height * 0.52,
          pictureKey: this.puzzleKeys[this.currentStage - 1],
          boardSizePx: this.boardSizePx,
          gapPx: this.gapPx,
          onTilePressed: (tileId) => this.onTilePressed(tileId),
        });

        this.audio.unlock();
        this.view.setInputEnabled(true);
        this.startRun();
      });
    } else {
      this.audio.unlock();
      this.view.setInputEnabled(true);
      this.startRun();
    }
  }

  private startRun() {
    if (this.runState !== "idle") return;
    this.runState = "running";
    this.startMs = this.time.now;
    this.elapsedMs = 0;
    this.updateStageStatus();
    this.audio.playBgm("bgm", 0.45);
  }

  /** デバッグ用：タイマーを残り3秒に設定（Score Modeのみ） */
  setTimeTo3Seconds() {
    if (this.gameMode !== "score" || this.runState !== "running") return;
    // 残り3秒 = 経過時間を (TIME_LIMIT_MS - 3000) に設定
    this.elapsedMs = TIME_LIMIT_MS - 3000;
    this.startMs = this.time.now - this.elapsedMs;
  }

  private pauseRun() {
    if (this.runState !== "running") return;
    this.runState = "idle";
    this.view.setInputEnabled(false);
    this.audio.pauseAll();
    this.scene.get("UIScene").events.emit("setStatus", { 
      status: "Paused",
      currentStage: this.currentStage,
      totalStages: this.puzzleKeys.length,
      clearedCount: this.clearedStages.length,
      gameMode: this.gameMode
    });
  }

  private resumeRun() {
    if (this.runState !== "idle") return;
    this.runState = "running";
    this.startMs = this.time.now - this.elapsedMs;
    this.view.setInputEnabled(true);
    this.audio.resumeAll();
    this.audio.playBgm("bgm", 0.45);
    this.updateStageStatus();
  }

  private onTilePressed(tileId: number) {
    if (this.animating) return;
    if (this.runState !== "running") return;

    const prevState = this.model.getState();
    const tileIndex = prevState.board.indexOf(tileId);
    if (tileIndex < 0) return;

    const moved = this.model.tryMove(tileId);
    if (!moved) return;

    const nextState = this.model.getState();
    const toIndex = prevState.blankIndex;

    this.moves++;
    this.audio.playSfx("sfxMove", 0.7);

    const remainingMs = this.gameMode === "score" ? Math.max(0, TIME_LIMIT_MS - this.elapsedMs) : 0;
    this.scene.get("UIScene").events.emit("tick", {
      moves: this.moves,
      elapsedMs: this.elapsedMs,
      remainingMs,
      totalScore: this.totalScore,
      gameMode: this.gameMode,
    });

    this.animating = true;
    this.view.setInputEnabled(false);

    const solved = this.model.isSolved();

    this.view.animateTile(tileId, toIndex, nextState, () => {
      this.animating = false;
      if (solved) {
        this.finish(true);
      } else {
        this.view.setInputEnabled(true);
      }
    });
  }

  /** タイムアップ処理 */
  private onTimeUp() {
    if (this.runState !== "running") return;
    this.runState = "timeup";
    this.view.setInputEnabled(false);
    this.audio.stopBgm();
    this.audio.playBgm("bgmGameOver", 0.5);

    // 正しい位置のタイル数でスコア計算
    const correctTiles = this.model.countCorrectTiles();
    const multiplier = this.getMultiplierForStage(this.currentStage);
    const stageScore = Math.floor(correctTiles * 5 * multiplier);
    this.totalScore += stageScore;

    const isLast = this.currentStage >= this.puzzleKeys.length;

    // FREE MODEではタイムアップは発生しない（前提条件）

    this.scene.get("UIScene").events.emit("showCongrats", {
      time: TIME_LIMIT_MS / 1000,
      moves: this.moves,
      stage: this.currentStage,
      isLast,
      isTimeUp: true,
      stageScore,
      totalScore: this.totalScore,
      correctTiles,
      gameMode: this.gameMode,
    });

    // Game Over時にgame_endを呼ぶ（Score Modeのゲームオーバー）
    // Back to Topを押した時にはgame_endを呼ばない（既に呼ばれているため）
    try {
      GameBox.end(this.totalScore, () => {
        // game_endのコールバック（ここでは何もしない）
        // Back to Topを押した時にrestart()が呼ばれる
      });
    } catch (e) {
      console.error("[GameScene] onTimeUp - GameBox.endエラー:", e);
    }
  }

  /** クリア処理 */
  private finish(cleared: boolean) {
    if (this.runState !== "running") return;
    this.runState = "finished";
    this.view.setInputEnabled(false);
    this.elapsedMs = this.time.now - this.startMs;
    this.audio.playSfx("sfxFinish", 0.8);
    this.audio.stopBgm();

    // クリアしたステージを記録
    if (!this.clearedStages.includes(this.currentStage)) {
      this.clearedStages.push(this.currentStage);
    }

    // スコア計算
    let stageScore = 0;
    if (this.gameMode === "score" && cleared) {
      const remainingSec = Math.max(0, (TIME_LIMIT_MS - this.elapsedMs) / 1000);
      const multiplier = this.getMultiplierForStage(this.currentStage);
      stageScore = Math.floor(remainingSec * 10 * multiplier);
      this.totalScore += stageScore;
    }

    this.view.showLastPiece(() => {
      this.view.mergeToFullImage(() => {
        const timeSec = Math.max(0, this.elapsedMs / 1000);
        const isLast = this.currentStage >= this.puzzleKeys.length;

        this.scene.get("UIScene").events.emit("showCongrats", {
          time: timeSec,
          moves: this.moves,
          stage: this.currentStage,
          isLast,
          isTimeUp: false,
          stageScore,
          totalScore: this.totalScore,
          correctTiles: this.model.countCorrectTiles(),
          gameMode: this.gameMode,
        });

        // 最後のステージをクリアした場合でも、ここではgame_endを呼ばない
        // ユーザーが「Back to Top」ボタンを押した時にonBackToTop()で処理する
      });
    });
  }

  /** 次のステージを準備 */
  prepareNextStage() {
    if (this.currentStage >= this.puzzleKeys.length) return;

    this.audio.stopAll();
    this.currentStage++;
    this.runState = "idle";
    this.animating = false;
    this.moves = 0;
    this.elapsedMs = 0;

    const n = this.getSizeForStage(this.currentStage);

    if (this.view) {
      this.view.destroy();
    }
    const { width, height } = this.scale;
    this.view = new PuzzleView({
      scene: this,
      x: width / 2,
      y: height * 0.52,
      pictureKey: this.puzzleKeys[this.currentStage - 1],
      boardSizePx: this.boardSizePx,
      gapPx: this.gapPx,
      onTilePressed: (tileId) => this.onTilePressed(tileId),
    });

    this.model.resetSolved(n);
    this.model.shuffle(this.shuffleSteps);
    const st = this.model.getState();
    this.view.build(st);

    const remainingMs = this.gameMode === "score" ? TIME_LIMIT_MS : 0;
    this.scene.get("UIScene").events.emit("tick", {
      moves: this.moves,
      elapsedMs: 0,
      remainingMs,
      totalScore: this.totalScore,
      gameMode: this.gameMode,
    });
    this.updateStageStatus();
    this.view.setInputEnabled(false);
  }

  /** プレビュー機能のトグル */
  private togglePreview() {
    if (this.view) {
      this.view.togglePreview();
    }
  }

  /** デバッグ用：即座に完成状態にする */
  instantSolve() {
    if (this.runState === "finished" || this.runState === "timeup") return;

    if (this.runState === "idle") {
      this.audio.unlock();
      this.startRun();
    }

    const n = this.getSizeForStage(this.currentStage);
    this.model.resetSolved(n);
    this.view.syncPositions(this.model.getState());
    this.finish(true);
  }

  /** デバッグ用：最後のステージに設定（全ステージクリアテスト用） */
  setToLastStage() {
    if (this.puzzleKeys.length === 0) return;
    if (this.currentStage >= this.puzzleKeys.length) return; // 既に最後のステージの場合
    
    // 最後のステージに設定
    this.currentStage = this.puzzleKeys.length;
    
    // viewを再作成（最後のステージの画像で）
    const n = this.getSizeForStage(this.currentStage);
    
    if (this.view) {
      this.view.destroy();
    }
    const { width, height } = this.scale;
    this.view = new PuzzleView({
      scene: this,
      x: width / 2,
      y: height * 0.52,
      pictureKey: this.puzzleKeys[this.currentStage - 1],
      boardSizePx: this.boardSizePx,
      gapPx: this.gapPx,
      onTilePressed: (tileId) => this.onTilePressed(tileId),
    });

    if (!this.model) {
      this.model = new PuzzleModel(n);
    } else {
      this.model.resetSolved(n);
    }
    this.model.shuffle(this.shuffleSteps);
    const st = this.model.getState();
    this.view.build(st);
    
    this.updateStageStatus();
    this.view.setInputEnabled(false);
    this.runState = "idle";
    this.moves = 0;
    this.elapsedMs = 0;
    
    console.log(`[GameScene] デバッグ: 最後のステージに設定しました (Stage ${this.currentStage}/${this.puzzleKeys.length})`);
  }

  /** 【FREE MODE非アクティブ化】保存データがあるかチェック（Free Mode用） */
  // GMOの要請により非アクティブ化（復元可能な形でコメントアウト）
  private hasSavedFreeData(): boolean {
    // FREE MODEは非アクティブ化されているため、常にfalseを返す
    // 復元時は以下のコメントを解除
    return false; // 復元時はこの行を削除して、以下のコメントを解除
    /*
    return this.savedData !== null && this.savedData.gameMode === "free";
    */
  }

  /** 保存データから続きを開始 */
  private continueFromSaved() {
    if (!this.savedData) {
      console.warn("[GameScene] continueFromSaved: 保存データがありません");
      return;
    }

    console.log("[GameScene] continueFromSaved: 保存データから続きを開始", this.savedData);

    // 保存データから状態を復元
    this.gameMode = this.savedData.gameMode;
    this.currentStage = this.savedData.currentStage;
    this.clearedStages = [...this.savedData.clearedStages];
    this.puzzleKeys = [...this.savedData.puzzleKeys];
    this.totalScore = 0; // スコアはリセット

    if (this.savedData.puzzleSize) {
      this.puzzleSize = this.savedData.puzzleSize;
    }

    // データの検証と修正
    if (this.currentStage < 1 || this.currentStage > this.puzzleKeys.length) {
      console.warn("[GameScene] continueFromSaved: currentStageが範囲外、1にリセット");
      this.currentStage = 1;
    }

    // clearedStagesから無効な値をフィルタリング
    this.clearedStages = this.clearedStages.filter(
      (stage) => stage >= 1 && stage <= this.puzzleKeys.length
    );

    // 全クリ後のデータを検出した場合、リセット
    const isAllCleared = this.clearedStages.length >= this.puzzleKeys.length;
    if (isAllCleared) {
      console.log("[GameScene] continueFromSaved: 全クリ後のデータを検出、リセット");
      this.currentStage = 1;
      this.clearedStages = [];
    }

    // 必要なpuzzle画像を読み込んでから開始
    this.loadPuzzleImages()
      .then(() => {
        console.log("[GameScene] continueFromSaved: 画像読み込み完了");
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("hideLoading");
          this.updateStageStatus();
          uiScene.events.emit("tick", {
            moves: 0,
            elapsedMs: 0,
            remainingMs: this.gameMode === "score" ? TIME_LIMIT_MS / 1000 : 0,
            totalScore: this.totalScore,
            gameMode: this.gameMode,
          });
        }
        this.resetAndWaitStart();
      })
      .catch((error) => {
        console.error("[GameScene] continueFromSaved: 画像読み込みエラー", error);
        const uiScene = this.scene.get("UIScene");
        if (uiScene) {
          uiScene.events.emit("hideLoading");
        }
      });
  }

  /** 進捗を保存 */
  private saveProgress(onSaved?: () => void) {
    this.saveProgressData([], onSaved);
  }

  /** 進捗データを保存 */
  private saveProgressData(completedSizes: (2 | 3 | 4 | 5 | 6)[], onSaved?: () => void) {
    // データの検証と修正
    let currentStage = this.currentStage;
    let clearedStages = [...this.clearedStages];

    // currentStageが範囲外の場合は1にリセット
    if (currentStage < 1 || currentStage > this.puzzleKeys.length) {
      console.warn("[GameScene] saveProgressData: currentStageが範囲外、1にリセット");
      currentStage = 1;
    }

    // clearedStagesから無効な値をフィルタリング
    clearedStages = clearedStages.filter(
      (stage) => stage >= 1 && stage <= this.puzzleKeys.length
    );

    // 全クリ後のデータを検出した場合、リセット
    const isAllCleared = clearedStages.length >= this.puzzleKeys.length;
    if (isAllCleared) {
      console.log("[GameScene] saveProgressData: 全クリ後のデータを検出、リセット");
      currentStage = 1;
      clearedStages = [];
    }

    const data: GameSaveData = {
      currentStage,
      puzzleKeys: [...this.puzzleKeys],
      clearedStages,
      gameMode: this.gameMode,
      puzzleSize: this.gameMode === "free" ? this.puzzleSize : undefined,
      completedSizes: completedSizes.length > 0 ? completedSizes : undefined,
    };

    // データサイズを確認（1024文字制限）
    console.log("[GameScene] 進捗保存:", data);

    // 最小限のデータ（clearedStagesなし）でサイズを確認
    const minimalData: GameSaveData = {
      currentStage,
      puzzleKeys: [...this.puzzleKeys],
      clearedStages: [],
      gameMode: this.gameMode,
      puzzleSize: this.gameMode === "free" ? this.puzzleSize : undefined,
    };
    const minimalJsonData = JSON.stringify(minimalData);

    if (minimalJsonData.length > 1024) {
      console.warn(
        "[GameScene] saveProgressData: 最小データでも1024文字を超えています:",
        minimalJsonData.length
      );
      // puzzleKeysを削減
      const maxKeys = Math.floor((1024 - minimalJsonData.length + JSON.stringify(this.puzzleKeys).length) / 20);
      if (maxKeys > 0) {
        data.puzzleKeys = data.puzzleKeys.slice(0, maxKeys);
      } else {
        console.error("[GameScene] saveProgressData: データサイズが大きすぎます");
        onSaved?.();
        return;
      }
    }

    // clearedStagesを含めたデータでサイズを確認
    const finalJsonData = JSON.stringify(data);
    if (finalJsonData.length > 1024) {
      console.warn(
        "[GameScene] saveProgressData: データサイズが1024文字を超えています:",
        finalJsonData.length,
        "clearedStagesを削除します"
      );
      // clearedStagesを削除
      data.clearedStages = [];
    }

    GameBox.save(data, () => {
      console.log("[GameScene] 保存完了");
      this.savedData = data;
      onSaved?.();
    });
  }

  /** Save and Exit */
  private saveAndExit() {
    // まず現在の進捗を保存（リセット前に保存する）
    this.saveProgress(() => {
      // 保存完了後、リセット処理を実行
      // タイムをリセット（0.00に）
      this.elapsedMs = 0;
      this.startMs = 0;
      // Stage進捗をリセット
      this.currentStage = 1;
      this.clearedStages = [];
      // 状態をリセット
      this.runState = "idle";
      this.moves = 0;
      
      // Saveボタンを非表示にする（UIScene側で処理）
      this.scene.get("UIScene").events.emit("hideSaveButton");
      
      // タイムを0.00としてUIに表示
      const remainingMs = this.gameMode === "score" ? Math.max(0, TIME_LIMIT_MS - this.elapsedMs) : 0;
      this.scene.get("UIScene").events.emit("tick", {
        moves: this.moves,
        elapsedMs: 0,
        remainingMs,
        totalScore: this.totalScore,
        gameMode: this.gameMode,
      });
      
      // Stage進捗を非表示にする
      this.scene.get("UIScene").events.emit("setStatus", { 
        status: "",
        currentStage: this.currentStage,
        totalStages: this.puzzleKeys.length,
        clearedCount: this.clearedStages.length,
        gameMode: this.gameMode
      });
      
      // game_endを呼ぶ
      GameBox.end(0, () => {
        // game_endのコールバック内でrestart()を呼ぶ
        this.showModeSelect();
      });
    });
  }

  /** 進捗を保存して終了（全クリ時など） */
  private saveProgressAndEnd(onSaved: () => void) {
    this.saveProgress(() => {
      // 保存完了後、game_endを呼ぶ
      GameBox.end(0, () => {
        // game_endのコールバック内でonSavedを呼ぶ
        onSaved();
      });
    });
  }
}
